import axios, { AxiosRequestConfig } from 'axios';
import { authenticate } from './wallet.api';
import { getAccessToken } from '@privy-io/react-auth';

interface RetryQueueItem {
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
  config: AxiosRequestConfig;
}

const client = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

client.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token && config.headers['Authorization'] !== null) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Create a list to hold the request queue
const refreshAndRetryQueue: RetryQueueItem[] = [];

// Flag to prevent multiple token refresh requests
let isRefreshing = false;

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest: AxiosRequestConfig = error.config;

    if (
      error.response &&
      [401, 403].includes(error.response.status) &&
      !error.config?.retried
    ) {
      error.config.retried = true;

      const accessToken = await getAccessToken();
      if (!isRefreshing && accessToken) {
        isRefreshing = true;
        try {
          // Refresh the access token
          const newAccessToken = await authenticate(accessToken, []);
          sessionStorage.setItem('token', newAccessToken.accessToken);

          // Update the request headers with the new access token
          error.config.headers['Authorization'] = `Bearer ${newAccessToken}`;

          // Retry all requests in the queue with the new token
          refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
            client
              .request(config)
              .then((response) => resolve(response))
              .catch((err) => reject(err));
          });

          // Clear the queue
          refreshAndRetryQueue.length = 0;

          // Retry the original request
          return client(originalRequest);
        } catch (refreshError) {
          // Handle token refresh error
          // You can clear all storage and redirect the user to the login page
          sessionStorage.removeItem('token');
          throw refreshError;
        } finally {
          isRefreshing = false;
        }
      }

      // Add the original request to the queue
      return new Promise<void>((resolve, reject) => {
        refreshAndRetryQueue.push({ config: originalRequest, resolve, reject });
      });
    }

    // Return a Promise rejection if the status code is not 401
    return Promise.reject(error);
  }
);

export default client;
